import React, { useCallback, useEffect, useRef, useState } from 'react';
import EcosystemFrame, { EcosystemTicker } from '../three/EcosystemFrame';
import FocusDrawer from './FocusDrawer';
import './command.css';

// The one-screen stage: scene in the middle, HUD panels pinned to the
// edges, an activity rail along the bottom, and a focus drawer that slides
// in with the existing deep section for whatever was clicked. The grid is
// sized to the viewport minus the page chrome above it (`chrome` px) so the
// page never scrolls; the drawer scrolls internally.
//
//   panels: [{ id, title, side: 'left'|'right', level?, body, onOpen? }]
//   onOpenDetail(id): navigate to the full page for a panel or stage card.
//           Return false to keep the drawer (hash links still open it).
//   drawer: { [id]: { title, render: () => node } } — hash links and anything
//           onOpenDetail does not claim
//   legend: [{ key, label, count }] — tone buckets drawn as chips over the
//           stage; clicking one filters the scene to that bucket
//   inspectorFor(sel): builds the card for a stage selection descriptor
//           ({ kind: 'node'|'host'|'center'|'flow'|'incident'|'anomaly', id })
//           from the CURRENT data, so a pinned card re-derives on every poll
//   defaultInspector: the card shown when nothing is hovered or selected
//   card shape: { title, subtitle, tone, rows: [[k, v]], sparks: [{ label,
//           values, color, last }], sections: [{ title, rows | dots | bars }],
//           issues: [{ level, text }], open: drawerId }
export const TONE_WORD = { ok: 'healthy', warn: 'degraded', down: 'down', busy: 'in progress', idle: 'idle', pool: 'pool', off: 'quiet' };

// Tiny inline sparkline: last N buckets, filled area, dot on the last.
function Spark({ values = [], color = 'ok', w = 92, h = 22 }) {
  const n = values.length;
  if (n < 2) return <svg className={`cmd-spark is-${color}`} width={w} height={h} aria-hidden />;
  const max = Math.max(1, ...values);
  const pts = values.map((v, i) => [(i / (n - 1)) * w, h - 1 - (Math.max(0, v) / max) * (h - 3)]);
  const d = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const [lx, ly] = pts[n - 1];
  return (
    <svg className={`cmd-spark is-${color}`} width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden>
      <path d={`${d} L${w},${h} L0,${h} Z`} className="cmd-spark-fill" />
      <path d={d} className="cmd-spark-line" />
      <circle cx={lx} cy={ly} r="1.8" className="cmd-spark-dot" />
    </svg>
  );
}

function Section({ s }) {
  return (
    <div className="cmd-inspector-section">
      <div className="cmd-inspector-section-title">{s.title}</div>
      {s.rows && <div className="cmd-inspector-rows">{s.rows.map(([k, v], i) => <React.Fragment key={`${k}-${i}`}><span>{k}</span><span>{v}</span></React.Fragment>)}</div>}
      {s.dots && <ul className="cmd-inspector-dots">{s.dots.map((d, i) => <li key={i}><i className={`cmd-dot is-${d.level}`} /><span>{d.text}</span></li>)}</ul>}
      {s.bars && (() => { const max = Math.max(1, ...s.bars.map((b) => b.value)); return (
        <div className="cmd-inspector-bars">
          {s.bars.map((b, i) => <div key={`${b.label}-${i}`} className="cmd-inspector-bar"><span>{b.label}</span><i style={{ width: `${Math.max(2, (b.value / max) * 100)}%` }} /><span>{b.text ?? b.value}</span></div>)}
        </div>
      ); })()}
    </div>
  );
}

// Two shapes for one card. The *brief* (hover, or the default when nothing
// is selected) is a two-line strip along the stage's bottom edge: head +
// the live issues and first rows inline — it covers a band of sky, never
// the rings. The *pinned* card (click) takes over the right HUD column:
// every row, sparklines, sections, scrolling, with the deep-dive button.
// The stage stays unobstructed; × or Esc brings the panels back.
const BRIEF_ROWS = 6;
const BRIEF_ISSUES = 3;
export function Brief({ card, hint, isDefault }) {
  const tone = card.tone || 'off';
  const rows = (card.rows || []).slice(0, BRIEF_ROWS);
  const issues = (card.issues || []).slice(0, BRIEF_ISSUES);
  return (
    <div className={`cmd-inspector is-brief is-${tone} ${isDefault ? 'is-default' : ''}`} aria-live="polite">
      <div className="cmd-inspector-head">
        <strong>{card.title}</strong>
        {TONE_WORD[tone] && <span className={`cmd-inspector-tone is-${tone}`}>{TONE_WORD[tone]}</span>}
        {card.subtitle ? <span className="cmd-inspector-sub">{card.subtitle}</span> : null}
        {hint ? <span className="cmd-inspector-hint">{hint}</span> : null}
      </div>
      {(issues.length > 0 || rows.length > 0) && (
        <div className="cmd-inspector-line">
          {issues.map((x, i) => <span key={`i-${x.kind}-${i}`} className="cmd-inspector-cell is-issue"><i className={`cmd-dot is-${x.level}`} /><span>{x.text}</span></span>)}
          {rows.map(([k, v], i) => <span key={`${k}-${i}`} className="cmd-inspector-cell"><span>{k}</span><span>{v}</span></span>)}
        </div>
      )}
    </div>
  );
}
export function Inspector({ card, hint, onOpen, onClear }) {
  const tone = card.tone || 'off';
  const rows = card.rows || [];
  const issues = card.issues || [];
  return (
    <div className={`cmd-inspector is-pinned is-${tone}`} aria-live="polite">
      <div className="cmd-inspector-head">
        <strong>{card.title}</strong>
        {TONE_WORD[tone] && <span className={`cmd-inspector-tone is-${tone}`}>{TONE_WORD[tone]}</span>}
        <button type="button" className="cmd-inspector-x" onClick={onClear} aria-label="Clear selection">×</button>
      </div>
      {card.subtitle ? <div className="cmd-inspector-sub">{card.subtitle}</div> : null}
      <div className="cmd-inspector-body">
        {rows.length > 0 && (
          <div className="cmd-inspector-rows">
            {rows.map(([k, v], i) => <React.Fragment key={`${k}-${i}`}><span>{k}</span><span>{v}</span></React.Fragment>)}
          </div>
        )}
        {issues.length > 0 && (
          <ul className="cmd-inspector-issues">
            {issues.map((x, i) => <li key={`${x.kind}-${i}`}><i className={`cmd-dot is-${x.level}`} /><span>{x.text}</span></li>)}
          </ul>
        )}
        {card.sparks?.length > 0 && (
          <div className="cmd-inspector-sparks">
            {card.sparks.map((sp) => (
              <div key={sp.label} className="cmd-inspector-spark">
                <Spark values={sp.values} color={sp.color} />
                <span className="cmd-inspector-spark-label">{sp.label}</span>
                <span className={`cmd-inspector-spark-last is-${sp.color}`}>{sp.last}</span>
              </div>
            ))}
          </div>
        )}
        {card.sections?.map((s) => <Section key={s.title} s={s} />)}
      </div>
      <div className="cmd-inspector-foot">
        {hint ? <span className="cmd-inspector-hint">{hint}</span> : null}
        {card.open && onOpen ? <button type="button" className="cmd-inspector-open" onClick={() => onOpen(card.open)}>Open details →</button> : null}
      </div>
    </div>
  );
}

// HUD columns scroll when the viewport is short; a fade at the clipped
// edge says "more below" instead of a panel cut mid-row.
const NARROW_PX = 1000;
function useOverflowClass(ref) {
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const mark = () => {
      el.classList.toggle('is-overflow', el.scrollHeight > el.clientHeight + 1);
      el.classList.toggle('is-at-end', el.scrollTop + el.clientHeight >= el.scrollHeight - 1);
    };
    mark();
    const ro = new ResizeObserver(mark);
    ro.observe(el);
    Array.from(el.children).forEach((c) => ro.observe(c));
    el.addEventListener('scroll', mark, { passive: true });
    return () => { ro.disconnect(); el.removeEventListener('scroll', mark); };
  });
}
function HudColumn({ side, children }) {
  const ref = useRef(null);
  useOverflowClass(ref);
  return <div ref={ref} className={`cmd-col cmd-col-${side}`}>{children}</div>;
}

export default function CommandView({
  data, derive, scene, panels = [], drawer = {}, events = [], caption = null, chrome = 176, className = '', onPick, onOpenDetail, children,
  telemetry = null, legend = null, columns = null, defaultInspector = null, inspectorFor = null, roomHref = null, initialSelected = null, wire = null,
}) {
  const simulated = telemetry?.source === 'sim';
  const [focus, setFocus] = useState(null);
  // Stage feedback: hover previews a card, click pins one (and focuses
  // the stage on it), the legend filter dims everything outside a tone.
  // Hover / selected are descriptors ({ kind, id }); the card itself is
  // rebuilt from the current data on every render so it stays live.
  // `initialSelected` is the pin carried back from the full-page room.
  const [hover, setHover] = useState(null);
  const [selected, setSelected] = useState(initialSelected);
  const [filter, setFilter] = useState(null);
  const onHover = useCallback((x) => setHover(x || null), []);
  const onSelect = useCallback((x) => setSelected((cur) => (x && cur && cur.kind === x.kind && cur.id === x.id ? null : x)), []);
  useEffect(() => { if (filter && legend && !legend.some((l) => l.key === filter && l.count > 0)) setFilter(null); }, [legend, filter]);
  // Hovering the selected object itself keeps the pinned card (no flicker to a preview).
  const sameAsSelected = !!(hover && selected && hover.kind === selected.kind && hover.id === selected.id);
  const hoverCard = hover && !sameAsSelected && inspectorFor ? inspectorFor(hover) : null;
  const selectedCard = selected && inspectorFor ? inspectorFor(selected) : null;
  // The selected object left the stage (instance deleted, incident closed) → drop the pin.
  useEffect(() => { if (selected && inspectorFor && !inspectorFor(selected)) setSelected(null); }, [selected, inspectorFor]);
  // Stage strip: the hovered object, else the default summary. The pinned
  // selection renders in the right HUD column (see below), never on the stage.
  const brief = hoverCard || defaultInspector;
  const captionText = caption ? `${simulated ? 'simulated · ' : ''}${caption}` : null;
  // The grid is viewport height minus whatever sits above it. Measure the
  // real offset (header rows, banners, help text vary per page) instead of
  // trusting the `chrome` estimate; re-measure on resize.
  const rootRef = useRef(null);
  const [top, setTop] = useState(null);
  // Narrow = the view itself (not the viewport) is under NARROW_PX — a
  // console sidebar plus an open copilot leaves ~900 px. Then the HUD
  // columns become one strip under the stage and the stage takes the
  // whole width, instead of a 400 px stage between two 236 px columns.
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const measure = () => {
      const el = rootRef.current; if (!el) return;
      setTop(Math.round(el.getBoundingClientRect().top + window.scrollY));
      setNarrow(el.clientWidth > 0 && el.clientWidth < NARROW_PX);
    };
    measure();
    const t = setTimeout(measure, 300);
    window.addEventListener('resize', measure);
    const ro = typeof ResizeObserver !== 'undefined' && rootRef.current ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(rootRef.current);
    return () => { clearTimeout(t); window.removeEventListener('resize', measure); if (ro) ro.disconnect(); };
  }, [chrome, panels.length]);
  // Deep links (#ap-health …) open the matching drawer entry in command mode.
  useEffect(() => {
    const apply = () => { const id = window.location.hash.replace('#', ''); if (id && drawer[id]) setFocus(id); };
    apply();
    window.addEventListener('hashchange', apply);
    return () => window.removeEventListener('hashchange', apply);
  }, [drawer]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // Escape peels one layer: drawer first, then the pinned selection.
      setFocus((f) => { if (f) return null; setSelected(null); return f; });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const left = panels.filter((p) => p.side !== 'right');
  const right = panels.filter((p) => p.side === 'right');
  const panel = (p) => (
    <button
      key={p.id}
      type="button"
      className={`cmd-panel is-${p.level || 'off'} ${p.wide ? 'is-wide' : ''}`}
      onClick={() => { if (p.onOpen) p.onOpen(); else if (onOpenDetail && onOpenDetail(p.id) !== false) return; else if (drawer[p.id]) setFocus(p.id); }}
      aria-label={`Open ${p.title}`}
    >
      <span className="cmd-panel-title"><i className={`cmd-dot is-${p.level || 'off'}`} />{p.title}</span>
      <span className="cmd-panel-body">{p.body}</span>
    </button>
  );

  return (
    <div ref={rootRef} className={`cmd-view ${className} ${simulated ? 'is-simulated' : ''} ${selectedCard ? 'is-pinned' : ''} ${narrow ? 'is-narrow' : ''}`} style={{ '--cmd-chrome': `${(top ?? chrome) + 12}px` }}>
      <div className="cmd-stage" onPointerLeave={() => setHover(null)}>
        <div className="cmd-stage-scene">
        <EcosystemFrame data={data} derive={derive} scene={scene} fill caption={false} ticker={false} telemetry={telemetry} wire={wire} roomHref={roomHref} onHover={inspectorFor ? onHover : undefined} onSelect={inspectorFor ? onSelect : undefined} selected={inspectorFor ? selected : null} hovered={inspectorFor ? hover : null} filter={filter} onPick={(id) => { if (onOpenDetail && onOpenDetail(id) !== false) return; if (onPick) onPick(id); else if (drawer[id]) setFocus(id); }} />
        {legend && data && (
          <div className="cmd-legend" role="group" aria-label="Filter the stage by state">
            {legend.map((l) => (
              <button
                key={l.key}
                type="button"
                className={`cmd-legend-chip ${filter === l.key ? 'is-active' : ''} ${l.count ? '' : 'is-empty'}`}
                onClick={() => setFilter((f) => (f === l.key ? null : l.key))}
                aria-pressed={filter === l.key}
                title={filter === l.key ? 'Show everything' : `Show only ${l.label}`}
              >
                <i className={`cmd-legend-dot is-${l.key}`} /><strong>{l.count}</strong>{l.label}
              </button>
            ))}
          </div>
        )}
        {columns && data && !legend && (
          <div className="cmd-legend cmd-columns" role="list" aria-label="Request path, left to right">
            {columns.map((c) => (
              <span key={c.key} role="listitem" className={`cmd-legend-chip is-passive is-${c.level}`} title={c.word}>
                <i className={`cmd-legend-dot is-${c.level}`} />{c.label}
              </span>
            ))}
          </div>
        )}
        {simulated && <div className="cmd-watermark" aria-label="Simulated data">SIMULATED{telemetry.scenario && telemetry.scenario !== 'calm' ? ` · ${telemetry.scenario}` : ''}</div>}
        </div>
        {data && brief && (
          <Brief
            card={brief}
            isDefault={!hoverCard}
            hint={hoverCard ? (selectedCard ? 'click to pin instead' : 'click to pin · double-click for details') : filter ? `showing only ${TONE_WORD[filter] || filter} · click the chip again for everything` : captionText || 'click anything on the stage for its live card'}
          />
        )}
        {caption && !brief && <div className="cmd-inspector is-brief is-off"><div className="cmd-inspector-head"><span className="cmd-inspector-hint">{captionText}</span></div></div>}
      </div>
      {narrow ? (
        <HudColumn side="strip">{[...left, ...right].map(panel)}</HudColumn>
      ) : <HudColumn side="left">{left.map(panel)}</HudColumn>}
      {selectedCard ? (
        <Inspector
          card={selectedCard}
          onOpen={(id) => { if (onOpenDetail && onOpenDetail(id) !== false) return; if (drawer[id]) setFocus(id); else if (onPick) onPick(id); }}
          onClear={() => setSelected(null)}
          hint="live · Esc clears"
        />
      ) : (!narrow && <HudColumn side="right">{right.map(panel)}</HudColumn>)}
      <div className="cmd-rail">
        {children}
        <EcosystemTicker events={events} limit={4} />
      </div>
      <FocusDrawer open={!!focus} title={focus ? drawer[focus]?.title : ''} onClose={() => { setFocus(null); if (window.location.hash) window.history.replaceState(null, '', window.location.pathname + window.location.search); }}>
        {focus && drawer[focus]?.render?.()}
      </FocusDrawer>
    </div>
  );
}
